// Small fetch client for our own JSON API calls. It keeps the slice of the axios API the app used:
//   http.get(url, config) / http.delete(url, config)
//   http.post(url, data, config) / http.put(...) / http.patch(...)
// Resolves to { data, status, statusText, headers }. Non-2xx responses reject with an HttpError whose
// `response` has the same shape, so `error.response?.data?.data?.message` keeps working.
// Config: `headers`, `signal`, `params`, and `data` as the body of a DELETE (as axios allowed).
//
// Inertia visits don't go through this; Inertia has its own client.

export class HttpError extends Error {
    constructor(message, { code, response, cause } = {}) {
        super(message, { cause });
        this.name = 'HttpError';
        this.code = code;
        this.response = response;
    }
}

export class CanceledError extends HttpError {
    constructor(cause) {
        super('canceled', { code: 'ERR_CANCELED', cause });
        this.name = 'CanceledError';
    }
}

const errorListeners = new Set();

// Runs for every failed request (network, HTTP status, abort) before the promise rejects.
export const onHttpError = (listener) => {
    errorListeners.add(listener);
    return () => errorListeners.delete(listener);
};

export const readCookie = (name) => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
};

const buildUrl = (url, params) => {
    if (!params) return url;
    const target = new URL(url, window.location.href);
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) target.searchParams.append(key, value);
    }
    return target.toString();
};

const isPassThroughBody = (data) => typeof data === 'string' || data instanceof FormData || data instanceof URLSearchParams || data instanceof Blob || data instanceof ArrayBuffer;

// Like axios: parse JSON whenever the body is JSON, otherwise keep the text.
const parseBody = (text) => {
    if (text === '') return text;
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
};

const fail = (error) => {
    for (const listener of errorListeners) listener(error);
    return Promise.reject(error);
};

const request = async (method, url, data, config) => {
    const { headers: configHeaders, signal, params } = config ?? {};
    const target = buildUrl(url, params);

    const headers = new Headers({
        Accept: 'application/json, text/plain, */*',
        'X-Requested-With': 'XMLHttpRequest',
    });
    // Laravel's CSRF check reads X-XSRF-TOKEN from the XSRF-TOKEN cookie, as axios sent it for same-origin calls.
    if (new URL(target, window.location.href).origin === window.location.origin) {
        const xsrf = readCookie('XSRF-TOKEN');
        if (xsrf) headers.set('X-XSRF-TOKEN', xsrf);
    }
    for (const [key, value] of Object.entries(configHeaders ?? {})) {
        if (value !== undefined && value !== null) headers.set(key, value);
    }

    let body;
    if (data !== undefined && data !== null) {
        if (isPassThroughBody(data)) {
            body = data;
        } else {
            body = JSON.stringify(data);
            if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
        }
    }

    let res;
    try {
        const init = { method, headers, signal, credentials: 'include' };
        if (body !== undefined) init.body = body;
        res = await fetch(target, init);
    } catch (cause) {
        if (cause?.name === 'AbortError') return fail(new CanceledError(cause));
        return fail(new HttpError('Network Error', { code: 'ERR_NETWORK', cause }));
    }

    let text;
    try {
        text = await res.text();
    } catch (cause) {
        if (cause?.name === 'AbortError') return fail(new CanceledError(cause));
        return fail(new HttpError('Network Error', { code: 'ERR_NETWORK', cause }));
    }

    const response = {
        data: parseBody(text),
        status: res.status,
        statusText: res.statusText,
        headers: Object.fromEntries(res.headers.entries()),
    };

    if (!res.ok) {
        const code = res.status >= 500 ? 'ERR_BAD_RESPONSE' : 'ERR_BAD_REQUEST';
        return fail(new HttpError(`Request failed with status code ${res.status}`, { code, response }));
    }

    return response;
};

const http = {
    get: (url, config) => request('GET', url, undefined, config),
    delete: (url, config) => request('DELETE', url, config?.data, config),
    post: (url, data, config) => request('POST', url, data, config),
    put: (url, data, config) => request('PUT', url, data, config),
    patch: (url, data, config) => request('PATCH', url, data, config),
};

export default http;
